import { localeHeaders, translate } from "@/i18n";
export type Staff = {
  id: number; email: string; name: string | null; active: boolean; is_admin?: boolean;
  // The modules (tabs) this person uses and where the app opens — the server enforces the same list.
  modules?: string[]; home_module?: string | null;
  // His viewer level: modules shown but not written (the server refuses their writes).
  read_only_modules?: string[];
};

// devise-jwt returns the token in the Authorization header on login, not in the body.
const TOKEN_KEY = "myotaku-warehouse:token";

// ⛔ Every read and write is guarded. localStorage throws in a private window and in some WebViews,
// and an auth layer that cannot even decide "logged out" is worse than one that has no storage.
export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* a session that lives only in memory still works until the tab closes */
  }
}

// A sign-in that lapses while the page is open (the token expires, or is revoked by a sign-out
// elsewhere): drop it and tell the app, which shows the sign-in screen instead of polling on with
// refusals. Nothing in his page signs in, so this is ours alone.
export const SIGNED_OUT = "myotaku-warehouse:signed-out";
export function expireSession() {
  writeToken(null);
  window.dispatchEvent(new Event(SIGNED_OUT));
}

export function authHeaders(): HeadersInit {
  const token = readToken();
  return token ? { Authorization: token } : {};
}

export class AuthError extends Error {}

export async function login(email: string, password: string): Promise<Staff> {
  const res = await fetch("/api/v1/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...localeHeaders() },
    body: JSON.stringify({ staff: { email, password } }),
  });

  if (res.status === 401) throw new AuthError(translate("login.wrong"));
  if (!res.ok) throw new Error(translate("login.failedStatus", { status: res.status }));

  // The header is the whole point of the request; a 200 without one means the server is
  // misconfigured, and silently "succeeding" would leave the app unable to make a single call.
  const token = res.headers.get("Authorization");
  if (!token) throw new Error(translate("login.noToken"));

  writeToken(token);
  return (await res.json()).staff as Staff;
}

export async function logout(): Promise<void> {
  try {
    await fetch("/api/v1/auth/logout", { method: "DELETE", headers: authHeaders() });
  } finally {
    // Drop the token whatever the server said. If revocation failed, the token still expires, and
    // leaving it here would mean the person is told they signed out while the app still acts
    // signed in.
    writeToken(null);
  }
}

export async function fetchMe(): Promise<Staff | null> {
  if (!readToken()) return null;
  const res = await fetch("/api/v1/me", { headers: { Accept: "application/json", ...localeHeaders(), ...authHeaders() } });
  if (res.status === 401) {
    writeToken(null);
    return null;
  }
  if (!res.ok) throw new Error(translate("login.sessionCheck", { status: res.status }));
  return (await res.json()).staff as Staff;
}
